import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import PlanTaskWidget, { stepsDone as stepsDoneCount, type TaskWidget } from './components/PlanTaskWidget'
import RevisionSubmitWidget from './components/RevisionSubmitWidget'
import { plan_top_down_path, plan_top_down_submit_path, plan_top_down_review_path, plan_top_down_by_channel_path, plan_top_down_complete_overall_path, plan_top_down_complete_channel_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import PeriodPicker from './components/PeriodPicker'
import StepProgress from './components/StepProgress'
import ChannelMatrix from './components/ChannelMatrix'
import EditorHeader, { DraftActivityBanner, RevisionBanner } from './components/EditorHeader'
import { formatStatDate } from './components/HeaderStat'
import { formatRevenueFull } from './components/formatters'
import PlanSwitcher, { type PlanSwitcherData } from './components/PlanSwitcher'
import RevisionRequestBanner from './components/RevisionRequestBanner'
import { type EditorRequest, type RequestComment } from './components/revisionRequest'
import { useRowNotes } from './components/useRowNotes'
import { revisionLabels } from './components/periodLabel'

interface Channel { id: string; name: string; slug: string; position: number }
interface PlanNodeFromServer {
  id: string
  name: string
  shortName?: string | null
  targetRevenue: number
  children: PlanNodeFromServer[]
}
interface MatrixRow {
  plan_node_id: string
  past: Record<string, string>
  plan: Record<string, number>
  // Actual sales for the plan's actual months. Present only on a revision.
  actual?: Record<string, string>
}

interface Props {
  slug: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    overall_completed_at: string | null
    channel_step_completed_at: string | null
    quarterly_step_completed_at: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  begin_date: string
  end_date: string
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  plan_switcher: PlanSwitcherData
  // Every open planning task the viewer is a PIC of (Plan::CycleTaskProps); empty for everyone else.
  task_widgets: TaskWidget[]
  // Published plan with no planning task open: the Submit-for-review control.
  revision_submit: boolean
  channels: Channel[]
  plan_nodes: PlanNodeFromServer[]
  matrix: { rows: MatrixRow[] }
  // The draft this page edits; its id rides along with every autosave.
  version: { id: string } | null
  // The draft is a revision of an already-published plan.
  revision: boolean
  // Someone else's unsubmitted edits in the shared draft (server omits it
  // when the viewer made the last edit).
  draft_activity?: { by: string; at: string } | null
  // The open sent request on this plan, this page's comments only. Null
  // when there is no open request.
  revision_request: EditorRequest | null
  review_comments?: RequestComment[]
}

function flattenPlanNodes(
  nodes: PlanNodeFromServer[],
  parentId: string | null = null,
  depth = 0
): { id: string; parentId: string | null; name: string; depth: number; hasChildren: boolean; targetRevenue: number }[] {
  return nodes.flatMap((n) => [
    {
      id: n.id, parentId,
      name: n.shortName?.trim() ? n.shortName : n.name,
      depth,
      hasChildren: (n.children?.length ?? 0) > 0,
      targetRevenue: n.targetRevenue
    },
    ...flattenPlanNodes(n.children ?? [], n.id, depth + 1)
  ])
}

function formatPeriod(begin_date: string, end_date: string): string {
  const fmt = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${fmt(begin_date)} – ${fmt(end_date)}`
}

export default function ByChannel({ slug, top_down_plan, begin_date, end_date, period, plan_period, actuals_months, plan_switcher, channels, plan_nodes, matrix, version, revision, draft_activity, task_widgets, revision_submit, revision_request, review_comments }: Props) {
  const market = useMarket()
  const rowNotes = useRowNotes(revision_request, review_comments)
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  // Full year = actual sales (top-level nodes only, to avoid double-counting
  // descendants) + this version's plan target.
  const fullYearTarget = useMemo(() => {
    const rootIds = new Set(plan_nodes.map((n) => n.id))
    const actualTotal = matrix.rows.reduce((sum, r) => {
      if (!rootIds.has(r.plan_node_id) || !r.actual) return sum
      return sum + Object.values(r.actual).reduce((s, v) => s + (Number(v) || 0), 0)
    }, 0)
    return actualTotal + top_down_plan.revenue_target
  }, [matrix.rows, plan_nodes, top_down_plan.revenue_target])
  const [overallCompletedAt] = useState<string | null>(top_down_plan.overall_completed_at)
  const [channelCompletedAt, setChannelCompletedAt] = useState<string | null>(top_down_plan.channel_step_completed_at)
  const [channelSaveStatus, setChannelSaveStatus] = useState<{ saving: boolean; savedAt: Date | null }>({ saving: false, savedAt: null })
  const handleSaveStatusChange = useCallback(
    (status: { saving: boolean; savedAt: Date | null }) => setChannelSaveStatus(status),
    []
  )
  const handleCompletionChange = useCallback(
    (next: string | null) => setChannelCompletedAt(next),
    []
  )
  const channelSaveLabel = channelSaveStatus.saving
    ? 'Saving…'
    : channelSaveStatus.savedAt
      ? `Saved ${channelSaveStatus.savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : ''

  const handleCompleteOverall = async () => {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    await fetch(plan_top_down_complete_overall_path(market.slug, slug), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' },
      credentials: 'same-origin'
    })
  }

  const handleCompleteChannel = async () => {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    const res = await fetch(plan_top_down_complete_channel_path(market.slug, slug), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' },
      credentials: 'same-origin'
    })
    if (res.ok) {
      const data = await res.json()
      setChannelCompletedAt(data.channel_step_completed_at)
    } else {
      const data = await res.json().catch(() => ({ error: 'Failed to complete channel breakdown.' }))
      const imbalances: Array<{
        scope: 'row' | 'column'
        node_path: string
        channel_name: string | null
        expected: number
        actual: number
        delta: number
      }> = data.imbalances ?? []
      if (imbalances.length > 0) {
        // For each channel, keep only the shallowest column imbalance — fixing the
        // parent column will usually resolve nested ones underneath.
        const shallowestColPath: Record<string, string> = {}
        imbalances.forEach((i) => {
          if (i.scope !== 'column' || !i.channel_name) return
          const cur = shallowestColPath[i.channel_name]
          if (!cur || i.node_path.length < cur.length) shallowestColPath[i.channel_name] = i.node_path
        })

        const seen = new Set<string>()
        const lines: string[] = []
        const fmt = (n: number) => formatRevenueFull(n, top_down_plan.currency)
        imbalances.forEach((i) => {
          if (i.scope === 'column') {
            if (!i.channel_name) return
            if (i.node_path !== shallowestColPath[i.channel_name]) return
          }
          const where = i.scope === 'row' ? i.node_path : `${i.node_path} > ${i.channel_name}`
          if (seen.has(where)) return
          seen.add(where)
          const status = i.delta > 0
            ? `${fmt(Math.abs(i.delta))} over`
            : `${fmt(Math.abs(i.delta))} remaining`
          lines.push(`${lines.length + 1}. ${where} — ${status}`)
        })
        window.alert(`${data.error}\n\n${lines.join('\n')}`)
      } else {
        window.alert(data.error ?? 'Failed to complete channel breakdown.')
      }
    }
  }

  const done = stepsDoneCount(overallCompletedAt, channelCompletedAt, top_down_plan.quarterly_step_completed_at)

  return (
    <AppLayout
      header={<EditorHeader
        name={top_down_plan.name}
        beginDate={begin_date}
        endDate={end_date}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={top_down_plan.revenue_target}
        currency={top_down_plan.currency}
        editPlanHref={plan_top_down_path(market.slug, slug)}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? fullYearTarget : null}
      />}
      headerActions={
        task_widgets.length > 0
          ? (
            <div className="flex flex-col gap-1.5">
              {task_widgets.map((w) => (
                <PlanTaskWidget
                  key={w.id}
                  task={w}
                  done={done}
                  submitHref={plan_top_down_submit_path(market.slug, slug, { task_id: w.id })}
                  reviewHref={plan_top_down_review_path(market.slug, slug, { task_id: w.id })}
                />
              ))}
            </div>
          )
          : revision_submit && <RevisionSubmitWidget done={done} submitHref={plan_top_down_submit_path(market.slug, slug)} />
      }
    >
      <Head title={`${top_down_plan.name} — Channels`} />
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="border rounded-lg px-4 py-2.5"
        >
          <PlanSwitcher data={plan_switcher} />
        </div>
        <div className="min-w-0 flex-1 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm leading-relaxed text-foreground">
          {periodLabels ? (
            <>Break down each product group's remaining-months target ({periodLabels.plan}) by sales channel. Actual sales for {periodLabels.actual} are shown above each cell.</>
          ) : (
            <>
              Break down each product group's target revenue by sales channel in {top_down_plan.org_node?.name ?? market.name} from {formatStatDate(begin_date)} – {formatStatDate(end_date)}.<br />
              After you finish, hit Complete button to move to step 3 (Channel Quarterly Breakdown).
            </>
          )}
        </div>
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="border rounded-lg px-4 py-2.5 ml-auto"
        >
          <PeriodPicker
            planId={top_down_plan.id}
            beginDate={begin_date}
            endDate={end_date}
            start={period.start}
            end={period.end}
            path={plan_top_down_by_channel_path(market.slug, slug)}
            only={['period', 'channels', 'matrix', 'plan_nodes']}
          />
        </div>
      </div>
      <StepProgress
        slug={slug}
        currentStep="channel"
        overallCompletedAt={overallCompletedAt}
        channelCompletedAt={channelCompletedAt}
        quarterlyCompletedAt={top_down_plan.quarterly_step_completed_at}
        onCompleteOverall={handleCompleteOverall}
        onCompleteChannel={handleCompleteChannel}
        channelStatusLabel={channelSaveLabel}
      />
      {revision && <RevisionBanner />}
      {revision_request && <RevisionRequestBanner request={revision_request} section="channel" slug={slug} />}
      {draft_activity && <DraftActivityBanner activity={draft_activity} />}

      <ChannelMatrix
        slug={slug}
        versionId={version?.id ?? null}
        channels={channels}
        planNodes={flattenPlanNodes(plan_nodes)}
        rows={matrix.rows}
        currency={top_down_plan.currency}
        rowNotes={rowNotes}
        periodLabels={periodLabels}
        onSaveStatusChange={handleSaveStatusChange}
        onCompletionChange={handleCompletionChange}
      />
    </AppLayout>
  )
}
